


















































import Captcha from '@integrations/002-Captcha/Captcha.vue';
import { Prop } from 'vue-property-decorator';
import Vue from 'vue';
import Component from 'vue-class-component';
import Heading from '@components/base/Heading.vue';
import ButtonComponent from '@components/004-ButtonComponent/ButtonComponent.vue';
import LoadingSpinner from '@components/012-LoadingSpinner/LoadingSpinner.vue';
import ShapeSVG from '@assets/svg/shapes/shape-a.svg';
import CheckmarkSVG from '@assets/svg/checkmark.svg';
import FormInput from '@modules/033-Form/components/FormInput.vue';
import IFormInput from '@frontend/types/modules/form/IFormInput';
import axios, { AxiosResponse } from 'axios';
import Sentry from '@libraries/sentry/browser';

@Component({
	hydration: true,
	componentId: 'M020',
	components: {
		Heading,
		ButtonComponent,
		LoadingSpinner,
		ShapeSVG,
		CheckmarkSVG,
		FormInput,
		Captcha
	}
})
export default class Newsletter extends Vue {
	@Prop(String)
	protected readonly headline!: string;

	@Prop(Number)
	protected readonly headlineLevel?: number;

	@Prop(String)
	protected readonly submissionUrl!: string;

	@Prop(String)
	protected readonly successMessage!: string;

	@Prop(Object)
	protected readonly fields!: INewsletterFields;

	protected internalFields = JSON.parse(JSON.stringify(this.fields));

	protected errorMessage: string | null = null;

	protected state: 'submitting' | 'error' | 'success' | 'input' = 'input';

	protected captchaSolution: string | null = null;

	protected async submit(): Promise<void> {
		if (!this.captchaSolution) {
			return;
		}

		const formData: FormData = new FormData();
		const baseURL: string = (window as any).HARIBO.global.env.SITE_BASE_URL;
		const useMock = baseURL.startsWith('/assets/mock-data');
		const url: string = useMock ? `${baseURL}/newsletter/success.json` : this.submissionUrl;

		this.errorMessage = null;
		this.state = 'submitting';

		formData.append('email', this.internalFields.email.value);
		formData.append('terms_accepted', '1');
		formData.append('captcha-solution', this.captchaSolution);

		try {
			const response: AxiosResponse = useMock ? await axios.get(url) : await axios.post(url, formData);

			if (response.status === 200) {
				const responseData: ISubmitResponseData = response.data;

				if (responseData.success) {
					this.state = 'success';
				} else {
					this.state = 'error';

					this.captchaSolution = null;
					this.captcha.reset();

					if (responseData.message) {
						this.generateErrorMessage(responseData.message);
					}
				}
			} else {
				this.state = 'error';

				this.captchaSolution = null;
				this.captcha.reset();

				Sentry.captureException(
					new Error(
						`Could not send form. HTTP Status: ${response.status}, Response body: ${
							response.data
						}, headers: ${JSON.stringify(response.headers)}`
					)
				);
			}
		} catch (error) {
			this.state = 'error';
			Sentry.captureException(error);
		}
	}

	protected snakeToCamel(value: string) {
		const arr = value.split('_');
		return arr
			.map((item: string, index: number) => {
				if (index === 0) return item.charAt(0).toLowerCase() + item.slice(1);
				return item.charAt(0).toUpperCase() + item.slice(1);
			})
			.join('');
	}

	protected generateErrorMessage(validationState: ValidationState) {
		const messageMap: Record<string, string> = {
			inputInvalid: this.i18n.newsletterInvalidEmailText,
			termsAcceptedInvalid: this.i18n.newsletterInvalidTermsText,
			alreadySubscribed: this.i18n.newsletterAlreadySubscribedText
		};

		let msg = messageMap[this.snakeToCamel(validationState)];
		msg = msg || this.i18n.newsletterUnknownErrorText;

		if (msg) this.errorMessage = msg;
	}

	protected onCaptchaSolved(solution: string): void {
		this.captchaSolution = solution;
	}

	protected get captcha(): Captcha {
		return this.$refs.captcha as Captcha;
	}
}

declare type ValidationState = 'terms_accepted_invalid' | 'already_subscribed' | 'input_invalid' | string;

interface INewsletterFields {
	email: IFormInput;
	termsConfirmation?: IFormInput;
	ageConfirmation?: IFormInput;
}

interface ISubmitResponseData {
	success: boolean;
	message?: ValidationState;
}
